import { Box, Stack, Typography } from "@mui/material";
import { SystemStyleObject, Theme } from "@mui/system";
import { LabellerTimelinePeriod } from "components/manualReview/components/LabellerTimelinePeriod";
import { Dayjs } from "dayjs";
import { useMemo } from "react";
import { LabellerPeriodWithExpandedEvents } from "../hooks/useTimelinesByLabeller";
import { sorted } from "helpers";
import { LabellerDetails } from "src/store/api/generatedApi";

interface LabellerTimelineRowProps {
  labellerDetails: LabellerDetails;
  periods: LabellerPeriodWithExpandedEvents[];
  visibleStart: Dayjs;
  visibleDuration: number;
  endAdornment?: React.ReactNode;
  currentTime?: number;
  showPlayhead?: boolean;
  trackSx?: SystemStyleObject<Theme>;
}

export const LabellerTimelineRow = ({
  labellerDetails,
  periods,
  visibleStart,
  visibleDuration,
  endAdornment,
  currentTime = 0,
  showPlayhead = false,
  trackSx,
}: LabellerTimelineRowProps) => {
  const mergedPeriods = useMemo(() => {
    const sortedPeriods = sorted(periods, (p) => p.start.valueOf());
    return sortedPeriods.reduce<LabellerPeriodWithExpandedEvents[]>(
      (agg, curr) => {
        if (agg.length === 0) {
          return [curr];
        }
        const lastPeriod = agg[agg.length - 1];
        if (
          lastPeriod?.end &&
          Math.abs(lastPeriod.end.diff(curr.start, "second")) < 10
        ) {
          const newLastPeriod = {
            ...lastPeriod,
            end: curr.end,
            events: [...lastPeriod.events, ...curr.events],
            periodIds: [...lastPeriod.periodIds, ...curr.periodIds],
          };
          return [...agg.slice(0, -1), newLastPeriod];
        } else {
          return [...agg, curr];
        }
      },
      []
    );
  }, [periods]);

  if (periods.length === 0 || visibleDuration <= 1) {
    return null;
  }

  const totalEvents = periods.reduce((sum, p) => sum + p.events.length, 0);
  const visibleEnd = visibleStart.add(visibleDuration, "second");

  const labellerIdentifier =
    (labellerDetails.labeller_type === "model" ? "🤖 " : "") +
    (labellerDetails.name ?? labellerDetails.id);

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mx: 1, mb: 1 }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flex: 1, fontSize: "0.7rem" }}
        >
          {labellerIdentifier} ({totalEvents})
        </Typography>
        {endAdornment}
      </Stack>
      <Box
        sx={{
          position: "relative",
          ...trackSx,
        }}
      >
        {mergedPeriods.map((period) => (
          <LabellerTimelinePeriod
            key={period.periodId}
            period={period}
            visibleStart={visibleStart}
            visibleEnd={visibleEnd}
            visibleDuration={visibleDuration}
            currentTime={currentTime}
            showPlayhead={showPlayhead}
          />
        ))}
      </Box>
    </Box>
  );
};
